/**
 * CanvasGrid - GPU-accelerated spreadsheet grid using Pixi.js
 *
 * Features:
 * - Virtual scrolling (only renders visible cells)
 * - Edge prefetch for smooth scrolling
 * - Shimmer loading placeholders
 * - Mouse wheel, touch, and keyboard navigation
 * - Custom scrollbars
 * - Resizable columns (drag header borders)
 * - Cell selection and Ctrl+C copy with headers
 */

import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { Application, Container, Graphics, Text, TextStyle } from 'pixi.js';
import {
  themes,
  CELL_WIDTH,
  CELL_HEIGHT,
  HEADER_HEIGHT,
  SCROLLBAR_WIDTH,
  SHIMMER_WIDTHS,
  getRowNumberWidth,
} from './types';

interface Selection {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

interface CanvasGridProps {
  columns: string[];
  totalRows: number;
  dataCache: Map<number, Record<string, unknown>>;
  isLoading: boolean;
  theme: 'dark' | 'light';
  onScroll: (startRow: number, endRow: number, startCol: number, endCol: number) => void;
  onCellHover?: (row: number, col: number, value: unknown, x: number, y: number) => void;
  onCellLeave?: () => void;
  onCellClick?: (row: number, col: number) => void;
  aiColumns?: Set<string>;
  aiColumnRefs?: Map<string, string>;
  aiLoading?: boolean;
}

export const CanvasGrid: React.FC<CanvasGridProps> = ({
  columns,
  totalRows,
  dataCache,
  isLoading,
  theme,
  onScroll,
  onCellHover,
  onCellLeave,
  onCellClick,
  aiColumns,
  aiColumnRefs,
  aiLoading,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const appRef = useRef<Application | null>(null);
  const gridContainerRef = useRef<Container | null>(null);
  const animationRef = useRef<number | null>(null);
  const onScrollRef = useRef(onScroll);
  onScrollRef.current = onScroll;
  const [isReady, setIsReady] = useState(false);

  // Scroll state
  const [scrollX, setScrollX] = useState(0);
  const [scrollY, setScrollY] = useState(0);

  // Touch state
  const touchStartRef = useRef({ x: 0, y: 0 });

  // Scrollbar drag state
  const [isDraggingVScroll, setIsDraggingVScroll] = useState(false);
  const [isDraggingHScroll, setIsDraggingHScroll] = useState(false);
  const dragStartRef = useRef({ scrollPos: 0, mousePos: 0 });

  // Column widths (per-column, resizable)
  const colWidthsRef = useRef<number[]>([]);
  const [colWidthsVersion, setColWidthsVersion] = useState(0); // trigger re-render on resize

  // Column resize state
  const [resizingCol, setResizingCol] = useState<number | null>(null);
  const resizeStartRef = useRef({ mouseX: 0, originalWidth: 0 });

  // Cell selection state
  const [selection, setSelection] = useState<Selection | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);

  // Cursor state
  const [cursorStyle, setCursorStyle] = useState('default');

  // Click tracking (distinguish click from drag)
  const clickDownCellRef = useRef<{ row: number; col: number } | null>(null);

  // Cached text styles to prevent memory leaks
  const textStylesRef = useRef<{
    text: TextStyle | null;
    header: TextStyle | null;
    muted: TextStyle | null;
    rowNum: TextStyle | null;
  }>({ text: null, header: null, muted: null, rowNum: null });

  const t = themes[theme];
  const rowNumWidth = getRowNumberWidth(totalRows);

  // Initialize/update column widths when columns change
  useEffect(() => {
    const current = colWidthsRef.current;
    if (current.length !== columns.length) {
      const newWidths = new Array(columns.length);
      for (let i = 0; i < columns.length; i++) {
        newWidths[i] = current[i] || CELL_WIDTH;
      }
      colWidthsRef.current = newWidths;
      setColWidthsVersion((v) => v + 1);
    }
  }, [columns.length]);

  // Cumulative column positions for fast lookup
  const colPositions = useMemo(() => {
    const widths = colWidthsRef.current;
    const positions = new Array(widths.length + 1);
    positions[0] = 0;
    for (let i = 0; i < widths.length; i++) {
      positions[i + 1] = positions[i] + widths[i];
    }
    return positions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colWidthsVersion, columns.length]);

  // Total content width from column positions
  const totalContentWidth = colPositions[columns.length] || 0;

  // Find column index at a given X offset from content start
  const getColAtX = useCallback((contentX: number): number => {
    // Binary search on colPositions
    let lo = 0;
    let hi = columns.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >>> 1;
      if (colPositions[mid + 1] <= contentX) {
        lo = mid + 1;
      } else if (colPositions[mid] > contentX) {
        hi = mid - 1;
      } else {
        return mid;
      }
    }
    return Math.min(Math.max(0, lo), columns.length - 1);
  }, [colPositions, columns.length]);

  // Update cached text styles when theme changes
  useEffect(() => {
    textStylesRef.current.text?.destroy();
    textStylesRef.current.header?.destroy();
    textStylesRef.current.muted?.destroy();
    textStylesRef.current.rowNum?.destroy();

    textStylesRef.current = {
      text: new TextStyle({
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, monospace',
        fontSize: 11,
        fill: t.text,
      }),
      header: new TextStyle({
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        fontSize: 11,
        fill: t.header,
        fontWeight: '500',
      }),
      muted: new TextStyle({
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, monospace',
        fontSize: 10,
        fill: t.textMuted,
      }),
      rowNum: new TextStyle({
        fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, monospace',
        fontSize: 10,
        fill: t.textMuted,
      }),
    };
  }, [t]);

  // Calculate max scroll
  const getMaxScrollY = useCallback(() => {
    const height = containerRef.current?.clientHeight || 600;
    return Math.max(0, totalRows * CELL_HEIGHT - height + HEADER_HEIGHT);
  }, [totalRows]);

  const getMaxScrollX = useCallback(() => {
    const width = containerRef.current?.clientWidth || 800;
    return Math.max(0, totalContentWidth - width + rowNumWidth);
  }, [totalContentWidth, rowNumWidth]);

  // Find visible column range using cumulative positions
  const getVisibleCols = useCallback((viewWidth: number): { startCol: number; endCol: number } => {
    const startCol = getColAtX(scrollX);
    // Find end col: keep going until cumulative position exceeds scrollX + viewWidth
    let endCol = startCol;
    const endX = scrollX + viewWidth - rowNumWidth;
    while (endCol < columns.length && colPositions[endCol] < endX) {
      endCol++;
    }
    endCol = Math.min(columns.length, endCol + 1); // +1 for partial column
    return { startCol, endCol };
  }, [scrollX, colPositions, columns.length, rowNumWidth, getColAtX]);

  // Render grid - core rendering function
  const renderGrid = useCallback(() => {
    const app = appRef.current;
    const gridContainer = gridContainerRef.current;
    if (!app || !gridContainer || !containerRef.current) return;

    while (gridContainer.children.length > 0) {
      const child = gridContainer.children[0];
      gridContainer.removeChild(child);
      child.destroy({ children: true, texture: true });
    }

    const width = app.renderer.width;
    const height = app.renderer.height;
    if (width === 0 || height === 0) return;

    const widths = colWidthsRef.current;
    const { startCol, endCol } = getVisibleCols(width);
    const startRow = Math.floor(scrollY / CELL_HEIGHT);
    const endRow = Math.min(totalRows, startRow + Math.ceil((height - HEADER_HEIGHT) / CELL_HEIGHT) + 2);

    onScrollRef.current(startRow, endRow, startCol, endCol);

    const textStyle = textStylesRef.current.text!;
    const headerStyle = textStylesRef.current.header!;
    const mutedStyle = textStylesRef.current.muted!;

    // Normalized selection bounds
    let selMinRow = -1, selMaxRow = -1, selMinCol = -1, selMaxCol = -1;
    if (selection) {
      selMinRow = Math.min(selection.startRow, selection.endRow);
      selMaxRow = Math.max(selection.startRow, selection.endRow);
      selMinCol = Math.min(selection.startCol, selection.endCol);
      selMaxCol = Math.max(selection.startCol, selection.endCol);
    }

    // Layer 1: Background
    const bg = new Graphics();
    bg.rect(0, 0, width, height).fill(t.bg);
    gridContainer.addChild(bg);

    // Layer 2: Grid lines
    const gridLines = new Graphics();

    // Vertical lines using cumulative column positions
    for (let col = startCol; col <= endCol; col++) {
      const x = rowNumWidth + colPositions[col] - scrollX;
      if (x >= rowNumWidth && x <= width) {
        gridLines.moveTo(x, HEADER_HEIGHT).lineTo(x, height).stroke({ width: 1, color: t.border, alpha: 0.5 });
      }
    }

    // Horizontal lines
    for (let row = startRow; row <= endRow + 1; row++) {
      const y = HEADER_HEIGHT + (row - startRow) * CELL_HEIGHT - (scrollY % CELL_HEIGHT);
      if (y >= HEADER_HEIGHT) {
        gridLines.moveTo(rowNumWidth, y).lineTo(width, y).stroke({ width: 1, color: t.border, alpha: 0.5 });
      }
    }

    gridLines.moveTo(0, HEADER_HEIGHT).lineTo(width, HEADER_HEIGHT).stroke({ width: 1, color: t.border });
    gridContainer.addChild(gridLines);

    // Layer 3: Selection highlight (below cells, above grid lines)
    if (selection) {
      const selGfx = new Graphics();
      for (let row = Math.max(startRow, selMinRow); row <= Math.min(endRow - 1, selMaxRow); row++) {
        const y = HEADER_HEIGHT + (row - startRow) * CELL_HEIGHT - (scrollY % CELL_HEIGHT);
        if (y < HEADER_HEIGHT - CELL_HEIGHT || y > height) continue;
        for (let col = Math.max(startCol, selMinCol); col <= Math.min(endCol - 1, selMaxCol); col++) {
          const cx = rowNumWidth + colPositions[col] - scrollX;
          const cw = widths[col];
          if (cx + cw < rowNumWidth || cx > width) continue;
          selGfx.rect(Math.max(rowNumWidth, cx), y, cw, CELL_HEIGHT).fill({ color: t.accent, alpha: 0.18 });
        }
      }
      // Selection border
      const borderMinX = Math.max(rowNumWidth, rowNumWidth + colPositions[selMinCol] - scrollX);
      const borderMaxX = rowNumWidth + colPositions[selMaxCol + 1] - scrollX;
      const borderMinY = HEADER_HEIGHT + (selMinRow - startRow) * CELL_HEIGHT - (scrollY % CELL_HEIGHT);
      const borderMaxY = HEADER_HEIGHT + (selMaxRow + 1 - startRow) * CELL_HEIGHT - (scrollY % CELL_HEIGHT);
      if (borderMaxX > rowNumWidth && borderMinX < width && borderMaxY > HEADER_HEIGHT && borderMinY < height) {
        selGfx.rect(borderMinX, borderMinY, borderMaxX - borderMinX, borderMaxY - borderMinY)
          .stroke({ width: 2, color: t.accent, alpha: 0.7 });
      }
      gridContainer.addChild(selGfx);
    }

    // Layer 4: Data cells (clipped)
    const cellContainer = new Container();
    const cellMask = new Graphics();
    cellMask.rect(rowNumWidth, HEADER_HEIGHT, width - rowNumWidth, height - HEADER_HEIGHT).fill(0xffffff);
    gridContainer.addChild(cellMask);
    cellContainer.mask = cellMask;
    gridContainer.addChild(cellContainer);

    const aiColSet = aiColumns || new Set<string>();

    for (let row = startRow; row < endRow; row++) {
      const y = HEADER_HEIGHT + (row - startRow) * CELL_HEIGHT - (scrollY % CELL_HEIGHT);
      if (y < HEADER_HEIGHT - CELL_HEIGHT || y > height) continue;

      const rowData = dataCache.get(row);
      if (rowData) {
        // Draw blue tint background for AI columns
        for (let col = startCol; col < endCol; col++) {
          if (aiColSet.has(columns[col])) {
            const colX = rowNumWidth + colPositions[col] - scrollX;
            if (colX + widths[col] > rowNumWidth && colX < width) {
              const tintBg = new Graphics();
              tintBg.rect(Math.max(rowNumWidth, colX), y, widths[col], CELL_HEIGHT).fill({ color: 0x3B82F6, alpha: 0.08 });
              cellContainer.addChild(tintBg);
            }
          }
        }
        for (let col = startCol; col < endCol; col++) {
          const cx = rowNumWidth + colPositions[col] - scrollX + 8;
          const cw = widths[col];
          if (cx < rowNumWidth || cx > width) continue;

          const colName = columns[col];
          const isAiCol = aiColSet.has(colName);
          const value = rowData[colName];
          const displayValue = value !== null && value !== undefined ? String(value).replace(/[\n\r]+/g, ' ') : '';

          if (isAiCol && !displayValue) {
            // Empty AI cell — show clickable placeholder only if referenced column has data
            const refCol = aiColumnRefs?.get(colName);
            const refVal = refCol ? rowData[refCol] : undefined;
            const refBlank = refCol !== undefined && (refVal === null || refVal === undefined || String(refVal).trim() === '');
            if (!refBlank) {
              const placeholder = new Text({ text: '\u2726 click to fill', style: mutedStyle });
              placeholder.x = cx;
              placeholder.y = y + 7;
              cellContainer.addChild(placeholder);
            }
          } else if (isAiCol && displayValue === '\u23F3') {
            // AI cell loading — shimmer
            const time = Date.now() / 1000;
            const shimmerPhase = (time + row * 0.08) % 1;
            const shimmerAlpha = 0.15 + Math.sin(shimmerPhase * Math.PI * 2) * 0.1;
            const shimmer = new Graphics();
            shimmer.roundRect(cx - 2, y + 6, Math.min(80, cw - 12), 12, 3).fill({ color: t.accent, alpha: shimmerAlpha });
            cellContainer.addChild(shimmer);
          } else if (displayValue) {
            const maxChars = Math.max(1, Math.floor((cw - 16) / 7));
            const truncated = displayValue.length > maxChars ? displayValue.slice(0, maxChars) + '\u2026' : displayValue;
            const text = new Text({ text: truncated, style: textStyle });
            text.x = cx;
            text.y = y + 6;
            cellContainer.addChild(text);
          }
        }
      } else {
        // No row data loaded yet — show shimmer for non-AI columns
        const time = Date.now() / 1000;
        const shimmerPhase = (time + row * 0.08) % 1;
        const shimmerAlpha = 0.12 + Math.sin(shimmerPhase * Math.PI * 2) * 0.08;

        for (let col = startCol; col < endCol; col++) {
          const colName = columns[col];
          const isAiCol = aiColSet.has(colName);
          const cx = rowNumWidth + colPositions[col] - scrollX + 6;
          if (cx < rowNumWidth) continue;

          if (isAiCol) {
            // AI column with no data loaded — show placeholder
            const placeholder = new Text({ text: '\u2726 click to fill', style: mutedStyle });
            placeholder.x = cx + 2;
            placeholder.y = y + 7;
            cellContainer.addChild(placeholder);
          } else {
            const shimmer = new Graphics();
            const w = SHIMMER_WIDTHS[(row + col) % SHIMMER_WIDTHS.length];
            shimmer.roundRect(cx, y + 6, Math.min(w, widths[col] - 12), 12, 3).fill({ color: t.textMuted, alpha: shimmerAlpha });
            cellContainer.addChild(shimmer);
          }
        }
      }
    }

    // Layer 5: Row numbers background
    const rowNumBg = new Graphics();
    rowNumBg.rect(0, HEADER_HEIGHT, rowNumWidth, height - HEADER_HEIGHT).fill(t.bgSecondary);
    gridContainer.addChild(rowNumBg);

    for (let row = startRow; row < endRow; row++) {
      const y = HEADER_HEIGHT + (row - startRow) * CELL_HEIGHT - (scrollY % CELL_HEIGHT);
      if (y < HEADER_HEIGHT - CELL_HEIGHT || y > height) continue;
      const rowNumText = (row + 1).toLocaleString();
      const rowNum = new Text({ text: rowNumText, style: mutedStyle });
      rowNum.x = rowNumWidth - rowNumText.length * 6 - 8;
      rowNum.y = y + 7;
      gridContainer.addChild(rowNum);
    }

    // Layer 6: Header background
    const headerBg = new Graphics();
    headerBg.rect(0, 0, width, HEADER_HEIGHT).fill(t.bgSecondary);
    gridContainer.addChild(headerBg);

    const cornerBg = new Graphics();
    cornerBg.rect(0, 0, rowNumWidth, HEADER_HEIGHT).fill(t.bgSecondary);
    gridContainer.addChild(cornerBg);

    // Header text + resize handles
    for (let col = startCol; col < endCol; col++) {
      const cx = rowNumWidth + colPositions[col] - scrollX + 8;
      if (cx < rowNumWidth - 8) continue;

      const colName = columns[col];
      if (!colName) continue;

      const isAi = aiColSet.has(colName);

      // Blue solid header background for AI columns
      if (isAi) {
        const colX = rowNumWidth + colPositions[col] - scrollX;
        const colW = widths[col];
        if (colX + colW > rowNumWidth && colX < width) {
          const aiHeaderBg = new Graphics();
          aiHeaderBg.rect(Math.max(rowNumWidth, colX), 0, colW, HEADER_HEIGHT).fill({ color: 0x3B82F6, alpha: 1 });
          gridContainer.addChild(aiHeaderBg);
        }
      }

      const prefix = isAi ? '\u2726 ' : '';
      const maxHeaderChars = Math.max(1, Math.floor((widths[col] - 16) / 7));
      const fullHeader = prefix + colName;
      const headerText = fullHeader.length > maxHeaderChars ? fullHeader.slice(0, maxHeaderChars) + '\u2026' : fullHeader;

      const text = new Text({ text: headerText, style: isAi
        ? new TextStyle({ fontFamily: headerStyle.fontFamily, fontSize: headerStyle.fontSize, fontWeight: '500' as const, fill: 0xFFFFFF })
        : headerStyle });
      text.x = Math.max(rowNumWidth + 8, cx);
      text.y = 8;
      gridContainer.addChild(text);

      // Resize handle visual (thin line at right edge of each header cell)
      const handleX = rowNumWidth + colPositions[col + 1] - scrollX;
      if (handleX >= rowNumWidth && handleX <= width) {
        const handle = new Graphics();
        handle.moveTo(handleX, 2).lineTo(handleX, HEADER_HEIGHT - 2)
          .stroke({ width: resizingCol === col ? 2 : 1, color: resizingCol === col ? t.accent : t.border, alpha: resizingCol === col ? 1 : 0.8 });
        gridContainer.addChild(handle);
      }
    }

    // Layer 7: Scrollbars
    const scrollbarSize = 10;
    const scrollbarPadding = 4;

    if (totalRows > 0) {
      const contentHeight = totalRows * CELL_HEIGHT;
      const viewportHeight = height - HEADER_HEIGHT - scrollbarSize - scrollbarPadding;
      const maxScrollY = getMaxScrollY();

      if (contentHeight > viewportHeight) {
        const scrollbarHeight = Math.max(40, (viewportHeight / contentHeight) * viewportHeight);
        const scrollbarY = HEADER_HEIGHT + (maxScrollY > 0 ? (scrollY / maxScrollY) * (viewportHeight - scrollbarHeight) : 0);

        const track = new Graphics();
        track.roundRect(width - scrollbarSize - scrollbarPadding, HEADER_HEIGHT, scrollbarSize, viewportHeight, 5)
          .fill({ color: t.bgSecondary, alpha: 0.8 });
        gridContainer.addChild(track);

        const thumb = new Graphics();
        thumb.roundRect(width - scrollbarSize - scrollbarPadding, scrollbarY, scrollbarSize, scrollbarHeight, 5)
          .fill({ color: t.textMuted, alpha: 0.8 });
        gridContainer.addChild(thumb);
      }
    }

    const viewportWidth = width - rowNumWidth - scrollbarSize - scrollbarPadding;
    const maxScrollX = getMaxScrollX();

    if (totalContentWidth > viewportWidth) {
      const scrollbarWidth = Math.max(40, (viewportWidth / totalContentWidth) * viewportWidth);
      const scrollbarX = rowNumWidth + (maxScrollX > 0 ? (scrollX / maxScrollX) * (viewportWidth - scrollbarWidth) : 0);

      const hTrack = new Graphics();
      hTrack.roundRect(rowNumWidth, height - scrollbarSize - scrollbarPadding, viewportWidth, scrollbarSize, 5)
        .fill({ color: t.bgSecondary, alpha: 0.8 });
      gridContainer.addChild(hTrack);

      const hThumb = new Graphics();
      hThumb.roundRect(scrollbarX, height - scrollbarSize - scrollbarPadding, scrollbarWidth, scrollbarSize, 5)
        .fill({ color: t.textMuted, alpha: 0.8 });
      gridContainer.addChild(hThumb);
    }
  }, [scrollX, scrollY, columns, totalRows, dataCache, isLoading, t, rowNumWidth,
    getMaxScrollY, getMaxScrollX, getVisibleCols, colPositions, totalContentWidth, selection, resizingCol, aiColumns, aiColumnRefs]);

  // Initialize Pixi.js
  useEffect(() => {
    let mounted = true;

    const initPixi = async () => {
      if (!canvasRef.current || !containerRef.current) return;

      try {
        const app = new Application();
        await app.init({
          canvas: canvasRef.current,
          width: containerRef.current.clientWidth || 800,
          height: containerRef.current.clientHeight || 600,
          backgroundColor: themes[theme].bg,
          antialias: true,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
        });

        if (!mounted) {
          app.destroy(true);
          return;
        }

        const gridContainer = new Container();
        app.stage.addChild(gridContainer);

        appRef.current = app;
        gridContainerRef.current = gridContainer;
        setIsReady(true);
      } catch (error) {
        console.error('Failed to initialize Pixi.js:', error);
      }
    };

    initPixi();

    return () => {
      mounted = false;
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (appRef.current) {
        appRef.current.destroy(true);
        appRef.current = null;
        gridContainerRef.current = null;
      }
      setIsReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle resize
  useEffect(() => {
    if (!isReady) return;

    const handleResize = () => {
      if (appRef.current && containerRef.current) {
        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        if (width > 0 && height > 0) {
          appRef.current.renderer.resize(width, height);
          renderGrid();
        }
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isReady, renderGrid]);

  // Re-render when data changes
  useEffect(() => {
    if (isReady) renderGrid();
  }, [isReady, renderGrid, dataCache]);

  // Update background color when theme changes
  useEffect(() => {
    if (isReady && appRef.current) {
      appRef.current.renderer.background.color = themes[theme].bg;
      renderGrid();
    }
  }, [isReady, theme, renderGrid]);

  // Shimmer animation (for data loading and AI cell loading)
  const needsAnimation = isLoading || aiLoading;
  useEffect(() => {
    if (!isReady || !needsAnimation) return;
    let frameId: number;
    const animate = () => {
      if (needsAnimation) {
        renderGrid();
        frameId = requestAnimationFrame(animate);
      }
    };
    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, [isReady, needsAnimation, renderGrid]);

  // Mouse wheel scroll
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleWheelNative = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setScrollX((prev) => Math.max(0, Math.min(prev + e.deltaX, getMaxScrollX())));
      setScrollY((prev) => Math.max(0, Math.min(prev + e.deltaY, getMaxScrollY())));
    };

    container.addEventListener('wheel', handleWheelNative, { passive: false });
    return () => container.removeEventListener('wheel', handleWheelNative);
  }, [getMaxScrollY, getMaxScrollX]);

  // Touch scroll
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const deltaX = touchStartRef.current.x - e.touches[0].clientX;
    const deltaY = touchStartRef.current.y - e.touches[0].clientY;
    setScrollX((prev) => Math.max(0, Math.min(prev + deltaX, getMaxScrollX())));
    setScrollY((prev) => Math.max(0, Math.min(prev + deltaY, getMaxScrollY())));
    touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
  }, [getMaxScrollY, getMaxScrollX]);

  // Copy selection to clipboard
  const copySelection = useCallback(() => {
    if (!selection) return;
    const minRow = Math.min(selection.startRow, selection.endRow);
    const maxRow = Math.max(selection.startRow, selection.endRow);
    const minCol = Math.min(selection.startCol, selection.endCol);
    const maxCol = Math.max(selection.startCol, selection.endCol);

    // Build header row
    const headers: string[] = [];
    for (let col = minCol; col <= maxCol; col++) {
      headers.push(columns[col] || '');
    }

    // Build data rows
    const rows: string[] = [headers.join('\t')];
    for (let row = minRow; row <= maxRow; row++) {
      const rowData = dataCache.get(row);
      const cells: string[] = [];
      for (let col = minCol; col <= maxCol; col++) {
        const value = rowData?.[columns[col]];
        cells.push(value !== null && value !== undefined ? String(value) : '');
      }
      rows.push(cells.join('\t'));
    }

    const text = rows.join('\n');
    navigator.clipboard.writeText(text).catch(() => {});
  }, [selection, columns, dataCache]);

  // Keyboard navigation + copy
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Copy
    if ((e.ctrlKey || e.metaKey) && e.key === 'c') {
      e.preventDefault();
      copySelection();
      return;
    }
    // Select all
    if ((e.ctrlKey || e.metaKey) && e.key === 'a') {
      e.preventDefault();
      if (columns.length > 0 && totalRows > 0) {
        setSelection({ startRow: 0, startCol: 0, endRow: totalRows - 1, endCol: columns.length - 1 });
      }
      return;
    }
    // Escape clears selection
    if (e.key === 'Escape') {
      setSelection(null);
      return;
    }

    const maxScrollY = getMaxScrollY();
    const maxScrollX = getMaxScrollX();
    const pageHeight = containerRef.current?.clientHeight || 600;
    const pageSize = Math.floor((pageHeight - HEADER_HEIGHT) / CELL_HEIGHT) * CELL_HEIGHT;

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        setScrollY((prev) => Math.max(0, prev - CELL_HEIGHT));
        break;
      case 'ArrowDown':
        e.preventDefault();
        setScrollY((prev) => Math.min(maxScrollY, prev + CELL_HEIGHT));
        break;
      case 'ArrowLeft':
        e.preventDefault();
        setScrollX((prev) => Math.max(0, prev - CELL_WIDTH));
        break;
      case 'ArrowRight':
        e.preventDefault();
        setScrollX((prev) => Math.min(maxScrollX, prev + CELL_WIDTH));
        break;
      case 'PageUp':
        e.preventDefault();
        setScrollY((prev) => Math.max(0, prev - pageSize));
        break;
      case 'PageDown':
        e.preventDefault();
        setScrollY((prev) => Math.min(maxScrollY, prev + pageSize));
        break;
      case 'Home':
        e.preventDefault();
        if (e.ctrlKey || e.metaKey) { setScrollY(0); setScrollX(0); }
        else setScrollY(0);
        break;
      case 'End':
        e.preventDefault();
        setScrollY(maxScrollY);
        break;
    }
  }, [getMaxScrollY, getMaxScrollX, copySelection, columns, totalRows]);

  // Scrollbar geometry helpers
  const getScrollbarGeometry = useCallback(() => {
    const container = containerRef.current;
    if (!container) return null;

    const width = container.clientWidth;
    const height = container.clientHeight;
    const scrollbarSize = 10;
    const scrollbarPadding = 4;

    const contentHeight = totalRows * CELL_HEIGHT;
    const viewportHeight = height - HEADER_HEIGHT - scrollbarSize - scrollbarPadding;
    const maxScrollY = getMaxScrollY();
    const vScrollbarHeight = Math.max(40, (viewportHeight / contentHeight) * viewportHeight);
    const vScrollbarY = HEADER_HEIGHT + (maxScrollY > 0 ? (scrollY / maxScrollY) * (viewportHeight - vScrollbarHeight) : 0);

    const viewportWidth = width - rowNumWidth - scrollbarSize - scrollbarPadding;
    const maxScrollX = getMaxScrollX();
    const hScrollbarWidth = Math.max(40, (viewportWidth / totalContentWidth) * viewportWidth);
    const hScrollbarX = rowNumWidth + (maxScrollX > 0 ? (scrollX / maxScrollX) * (viewportWidth - hScrollbarWidth) : 0);

    return {
      vertical: {
        trackX: width - scrollbarSize - scrollbarPadding,
        trackY: HEADER_HEIGHT,
        trackHeight: viewportHeight,
        thumbY: vScrollbarY,
        thumbHeight: vScrollbarHeight,
        visible: contentHeight > viewportHeight,
      },
      horizontal: {
        trackX: rowNumWidth,
        trackY: height - scrollbarSize - scrollbarPadding,
        trackWidth: viewportWidth,
        thumbX: hScrollbarX,
        thumbWidth: hScrollbarWidth,
        visible: totalContentWidth > viewportWidth,
      },
      scrollbarSize,
    };
  }, [totalRows, totalContentWidth, rowNumWidth, scrollX, scrollY, getMaxScrollX, getMaxScrollY]);

  // Determine cell from mouse position
  const getCellFromMouse = useCallback((clientX: number, clientY: number): { row: number; col: number } | null => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (x < rowNumWidth || y < HEADER_HEIGHT) return null;
    const contentX = x - rowNumWidth + scrollX;
    const col = getColAtX(contentX);
    const row = Math.floor((y - HEADER_HEIGHT + scrollY) / CELL_HEIGHT);
    if (col < 0 || col >= columns.length || row < 0 || row >= totalRows) return null;
    return { row, col };
  }, [scrollX, scrollY, rowNumWidth, columns.length, totalRows, getColAtX]);

  // Check if mouse is near a header column resize handle
  const getResizeColFromMouse = useCallback((clientX: number, clientY: number): number | null => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return null;
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    if (y > HEADER_HEIGHT || y < 0) return null;
    // Check each visible column's right edge
    const contentX = x - rowNumWidth + scrollX;
    for (let col = 0; col < columns.length; col++) {
      const rightEdge = colPositions[col + 1];
      if (Math.abs(contentX - rightEdge) <= 4) return col;
    }
    return null;
  }, [scrollX, rowNumWidth, columns.length, colPositions]);

  // Handle mouse down: scrollbar, resize, or selection
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const geo = getScrollbarGeometry();
    if (!geo) return;

    // Check scrollbars first
    if (geo.vertical.visible) {
      const { trackX, trackY, trackHeight, thumbY, thumbHeight } = geo.vertical;
      if (x >= trackX && x <= trackX + geo.scrollbarSize && y >= trackY && y <= trackY + trackHeight) {
        e.preventDefault();
        if (y >= thumbY && y <= thumbY + thumbHeight) {
          setIsDraggingVScroll(true);
          dragStartRef.current = { scrollPos: scrollY, mousePos: y };
        } else {
          const ratio = (y - trackY) / trackHeight;
          setScrollY(Math.max(0, Math.min(getMaxScrollY(), ratio * getMaxScrollY())));
        }
        return;
      }
    }

    if (geo.horizontal.visible) {
      const { trackX, trackY, trackWidth, thumbX, thumbWidth } = geo.horizontal;
      if (y >= trackY && y <= trackY + geo.scrollbarSize && x >= trackX && x <= trackX + trackWidth) {
        e.preventDefault();
        if (x >= thumbX && x <= thumbX + thumbWidth) {
          setIsDraggingHScroll(true);
          dragStartRef.current = { scrollPos: scrollX, mousePos: x };
        } else {
          const ratio = (x - trackX) / trackWidth;
          setScrollX(Math.max(0, Math.min(getMaxScrollX(), ratio * getMaxScrollX())));
        }
        return;
      }
    }

    // Check column resize
    const resizeCol = getResizeColFromMouse(e.clientX, e.clientY);
    if (resizeCol !== null) {
      e.preventDefault();
      setResizingCol(resizeCol);
      resizeStartRef.current = { mouseX: e.clientX, originalWidth: colWidthsRef.current[resizeCol] };
      return;
    }

    // Cell selection
    const cell = getCellFromMouse(e.clientX, e.clientY);
    if (cell) {
      e.preventDefault();
      clickDownCellRef.current = cell;
      if (e.shiftKey && selection) {
        // Extend selection
        setSelection((prev) => prev ? { ...prev, endRow: cell.row, endCol: cell.col } : null);
      } else {
        setSelection({ startRow: cell.row, startCol: cell.col, endRow: cell.row, endCol: cell.col });
      }
      setIsSelecting(true);
    } else {
      clickDownCellRef.current = null;
    }
  }, [scrollX, scrollY, getScrollbarGeometry, getMaxScrollX, getMaxScrollY,
    getResizeColFromMouse, getCellFromMouse, selection]);

  // Handle column resize and selection drag
  useEffect(() => {
    if (resizingCol === null && !isSelecting && !isDraggingVScroll && !isDraggingHScroll) return;

    const handleMouseMove = (e: MouseEvent) => {
      if (resizingCol !== null) {
        const delta = e.clientX - resizeStartRef.current.mouseX;
        const newWidth = Math.max(40, resizeStartRef.current.originalWidth + delta);
        colWidthsRef.current[resizingCol] = newWidth;
        setColWidthsVersion((v) => v + 1);
        return;
      }

      if (isSelecting) {
        const cell = getCellFromMouse(e.clientX, e.clientY);
        if (cell) {
          setSelection((prev) => prev ? { ...prev, endRow: cell.row, endCol: cell.col } : null);
        }
        return;
      }

      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;

      if (isDraggingVScroll) {
        const y = e.clientY - rect.top;
        const geo = getScrollbarGeometry();
        if (!geo) return;
        const delta = y - dragStartRef.current.mousePos;
        const maxScrollY = getMaxScrollY();
        const scrollRatio = delta / (geo.vertical.trackHeight - geo.vertical.thumbHeight);
        setScrollY(Math.max(0, Math.min(maxScrollY, dragStartRef.current.scrollPos + scrollRatio * maxScrollY)));
      }

      if (isDraggingHScroll) {
        const x = e.clientX - rect.left;
        const geo = getScrollbarGeometry();
        if (!geo) return;
        const delta = x - dragStartRef.current.mousePos;
        const maxScrollX = getMaxScrollX();
        const scrollRatio = delta / (geo.horizontal.trackWidth - geo.horizontal.thumbWidth);
        setScrollX(Math.max(0, Math.min(maxScrollX, dragStartRef.current.scrollPos + scrollRatio * maxScrollX)));
      }
    };

    const handleMouseUp = () => {
      setResizingCol(null);
      setIsSelecting(false);
      setIsDraggingVScroll(false);
      setIsDraggingHScroll(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingCol, isSelecting, isDraggingVScroll, isDraggingHScroll,
    getCellFromMouse, getScrollbarGeometry, getMaxScrollX, getMaxScrollY]);

  // Mouse move for hover and cursor
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Update cursor based on position
    const resizeCol = getResizeColFromMouse(e.clientX, e.clientY);
    if (resizeCol !== null) {
      setCursorStyle('col-resize');
    } else if (isDraggingVScroll || isDraggingHScroll) {
      setCursorStyle('grabbing');
    } else if (resizingCol !== null) {
      setCursorStyle('col-resize');
    } else {
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        if (x > rowNumWidth && y > HEADER_HEIGHT) {
          setCursorStyle('cell');
        } else {
          setCursorStyle('default');
        }
      }
    }

    if (!onCellHover) return;
    const cell = getCellFromMouse(e.clientX, e.clientY);
    if (cell) {
      const rowData = dataCache.get(cell.row);
      if (rowData) {
        onCellHover(cell.row, cell.col, rowData[columns[cell.col]], e.clientX, e.clientY);
        return;
      }
    }
    onCellLeave?.();
  }, [getResizeColFromMouse, getCellFromMouse, isDraggingVScroll, isDraggingHScroll,
    resizingCol, rowNumWidth, columns, dataCache, onCellHover, onCellLeave]);

  // Handle click (fires only when mousedown and mouseup are on the same cell)
  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!onCellClick) return;
    const cell = getCellFromMouse(e.clientX, e.clientY);
    const downCell = clickDownCellRef.current;
    if (cell && downCell && cell.row === downCell.row && cell.col === downCell.col) {
      onCellClick(cell.row, cell.col);
    }
  }, [onCellClick, getCellFromMouse]);

  // Auto-focus on mount for keyboard navigation
  useEffect(() => {
    if (isReady && containerRef.current) containerRef.current.focus();
  }, [isReady]);

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative outline-none"
      style={{
        minHeight: '400px',
        overflow: 'hidden',
        cursor: cursorStyle,
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
      tabIndex={0}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseLeave={onCellLeave}
    >
      <style>{`
        .canvas-grid-container::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
    </div>
  );
};

export default CanvasGrid;
